import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IoTrash, IoAdd, IoRemove, IoCart, IoArrowForward, IoHeart, IoCheckmarkCircle, IoShield, IoRefresh } from 'react-icons/io5';
import useCartStore from '@/stores/CartStore';
import useWishlistStore from '@/stores/WishlistStore';
import Button from '@/components/common/Button/Button';
import './Cart.css';

const Cart: React.FC = () => {
    const navigate = useNavigate();
    const {
        items,
        total_items,
        subtotal,
        tax_amount,
        shipping_cost,
        total_amount,
        updateQuantity,
        removeItem,
        clearCart,
    } = useCartStore();

    const { addItem: addToWishlist, hasItem: isInWishlist } = useWishlistStore();

    const handleQuantityChange = (itemId: string, newQuantity: number) => {
        if (newQuantity <= 0) {
            removeItem(itemId);
        } else {
            updateQuantity(itemId, newQuantity);
        }
    };

    const handleMoveToWishlist = (item: any) => {
        if (!isInWishlist(item.product_id)) {
            addToWishlist({
                id: item.product_id,
                product_id: item.product_id,
                product_name: item.product_name,
                product_image: item.product_image,
                base_price: item.base_price,
            });
        }
        removeItem(item.id);
    };

    const formatCustomization = (item: any) => {
        const customizations = [];
        if (item.material_name) customizations.push(`Material: ${item.material_name}`);
        if (item.color_name) customizations.push(`Color: ${item.color_name}`);
        if (item.size_name) customizations.push(`Tamaño: ${item.size_name}`);
        if (item.customization.custom_text) customizations.push(`Texto: "${item.customization.custom_text}"`);
        if (item.customization.custom_logo) customizations.push(`Logo: ${item.customization.custom_logo}`);
        if (item.customization.custom_pattern) customizations.push(`Patrón: ${item.customization.custom_pattern}`);
        return customizations;
    };

    if (items.length === 0) {
        return (
            <div className="cart_container">
                <div className="cart_empty_state">
                    <div className="cart_empty_icon">
                        <IoCart size={80} />
                    </div>
                    <h2 className="cart_empty_title">Tu carrito está vacío</h2>
                    <p className="cart_empty_description">Parece que aún no has agregado ningún producto a tu carrito. ¡Explora nuestro catálogo y encuentra productos increíbles!</p>
                    <div className="cart_empty_actions">
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={() => navigate('/products')}
                        >
                            <IoCart size={18} />
                            Comenzar a Comprar
                        </Button>
                        <Button
                            variant="secondary"
                            size="lg"
                            onClick={() => navigate('/categories')}
                        >
                            Explorar Categorías
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cart_container">
            <div className="cart_header">
                <h1>Carrito de Compras</h1>
                <p>Tienes {total_items} {total_items === 1 ? 'artículo' : 'artículos'} en tu carrito</p>
            </div>

            <div className="cart_content">
                <div className="cart_items_section">
                    <div className="cart_items_header">
                        <div className="cart_items_title">
                            <h3>Artículos en tu Carrito</h3>
                            <span className="cart_items_count">{total_items} {total_items === 1 ? 'artículo' : 'artículos'}</span>
                        </div>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={clearCart}
                        >
                            <IoTrash size={14} />
                            Vaciar Carrito
                        </Button>
                    </div>

                    <div className="cart_items">
                        {items.map((item) => (
                            <div key={item.id} className="cart_item">
                                <div className="cart_item_image">
                                    <img
                                        src={item.product_image || '/placeholder-product.jpg'}
                                        alt={item.product_name}
                                        onClick={() => navigate(`/products/${item.product_id}`)}
                                    />
                                    <div className="cart_item_overlay">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            className="view_product_btn"
                                            onClick={() => navigate(`/products/${item.product_id}`)}
                                        >
                                            Ver Producto
                                        </Button>
                                    </div>
                                </div>

                                <div className="cart_item_content">
                                    <div className="cart_item_header">
                                        <h4
                                            className="cart_item_name"
                                            onClick={() => navigate(`/products/${item.product_id}`)}
                                        >
                                            {item.product_name}
                                        </h4>
                                        <div className="cart_item_actions">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleMoveToWishlist(item)}
                                            >
                                                Mover a Favoritos
                                                <IoHeart size={16} />
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => removeItem(item.id)}
                                            >
                                                Eliminar
                                                <IoTrash size={16} />
                                            </Button>
                                        </div>
                                    </div>

                                    {formatCustomization(item).length > 0 && (
                                        <div className="cart_item_customization">
                                            <div className="customization_label">Personalización</div>
                                            <div className="customization_tags">
                                                {formatCustomization(item).map((custom, index) => (
                                                    <span key={index} className="customization_tag">
                                                        {custom}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {(item as any).special_notes && (
                                        <div className="cart_item_notes">
                                            <div className="notes_label">Notas Especiales</div>
                                            <p className="notes_text">{(item as any).special_notes}</p>
                                        </div>
                                    )}

                                    <div className="cart_item_bottom">
                                        <div className="cart_item_quantity">
                                            <div className="quantity_label">Cantidad</div>
                                            <div className="quantity_controls">
                                                <button
                                                    className="quantity_btn"
                                                    onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                                    disabled={item.quantity <= 1}
                                                >
                                                    <IoRemove size={16} />
                                                </button>
                                                <span className="quantity_value">{item.quantity}</span>
                                                <button
                                                    className="quantity_btn"
                                                    onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                                                >
                                                    <IoAdd size={16} />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="cart_item_pricing">
                                            <div className="unit_price">
                                                <div className="price_label">Precio Unitario</div>
                                                <span className="price_value">${item.unit_price.toFixed(2)}</span>
                                            </div>
                                            <div className="total_price">
                                                <div className="price_label">Total</div>
                                                <span className="price_value">${item.total_price.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="cart_summary_section">
                    <div className="cart_summary">
                        <h3>Resumen del Pedido</h3>
                        
                        <div className="summary_breakdown">
                            <div className="summary_line">
                                <span>Subtotal ({total_items} {total_items === 1 ? 'artículo' : 'artículos'})</span>
                                <span>${subtotal.toFixed(2)}</span>
                            </div>
                            <div className="summary_line">
                                <span>Impuestos</span>
                                <span>${tax_amount.toFixed(2)}</span>
                            </div>
                            <div className="summary_line">
                                <span>Envío</span>
                                <span>{shipping_cost > 0 ? `$${shipping_cost.toFixed(2)}` : 'Gratis'}</span>
                            </div>
                            {shipping_cost === 0 && (
                                <div className="free_shipping_note">
                                    Envío gratuito en pedidos superiores a $100
                                </div>
                            )}
                        </div>

                        <div className="summary_total">
                            <span>Total</span>
                            <span>${total_amount.toFixed(2)}</span>
                        </div>

                        <div className="cart_actions">
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={() => navigate('/products')}
                            >
                                Seguir Comprando
                            </Button>
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={() => navigate('/checkout')}
                            >
                                Proceder al Pago
                                <IoArrowForward size={18} />
                            </Button>
                        </div>
                        
                        <div className="cart_benefits">
                            <div className="benefit_item">
                                <IoCheckmarkCircle className="benefit_icon success" size={18} />
                                <span>Pago Seguro</span>
                            </div>
                            <div className="benefit_item">
                                <IoShield className="benefit_icon primary" size={18} />
                                <span>Garantía de Devolución</span>
                            </div>
                            <div className="benefit_item">
                                <IoRefresh className="benefit_icon accent" size={18} />
                                <span>Envío Rápido</span>
                            </div>
                        </div>
                    </div>

                    <div className="cart_recommendations">
                        <h4>Productos Recomendados</h4>
                        <div className="recommendations_placeholder">
                            Aquí aparecerán productos que podrían interesarte
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Cart;
